import * as THREE from 'three';
import { Engine } from './core/Engine.js';
import { EventBus, EV } from './core/EventBus.js';
import { Settings } from './core/Settings.js';
import { Save } from './core/Save.js';
import { PhysicsWorld } from './physics/PhysicsWorld.js';
import { PortalSystem } from './portals/PortalSystem.js';
import { MaterialLibrary } from './render/Materials.js';
import { Sky } from './render/Sky.js';
import { PostFX } from './render/PostFX.js';
import { InputManager } from './input/InputManager.js';
import { AudioEngine } from './audio/AudioEngine.js';
import { Player } from './entities/Player.js';
import { buildLevel } from './levels/LevelBuilder.js';
import { createMode } from './gameplay/GameModes.js';
import { LEVELS, LEVELS_BY_ID, nextLevel } from './levels/index.js';
import { HUD } from './ui/HUD.js';
import { Screens } from './ui/Screens.js';
import * as Fullscreen from './ui/Fullscreen.js';
import { Rival } from './entities/Rival.js';
import { GhostRecorder, GhostRider } from './gameplay/Ghost.js';
import { track, wireErrorReporting } from './core/Telemetry.js';
import pkg from '../package.json';
import { dailyCombo, applyMutator, applyPhysics } from './gameplay/Mutators.js';
import { generateGauntlet } from './gameplay/GauntletGen.js';
import { RaceClient, RemotePlayers, startOffset, PINGS } from './net/Race.js';
import {
  makeSyncCode, formatSyncCode, normalizeSyncCode,
  mergeSaves, mergeSettings, packPayload,
} from './core/Sync.js';
import { RivalAgent } from './ai/RivalAgent.js';
import { RivalBrain } from './ai/RivalBrain.js';
import { RIVAL_ROUTES } from './ai/RivalRoutes.js';
import { RivalVoice } from './ai/RivalVoice.js';
import { shareText, deliverLink } from './net/Share.js';
import { boardRefusalText } from './net/Scores.js';
import { redlineDef, redlineKey, hasRedline } from './gameplay/Redline.js';
import './gameplay/Prefabs.js';

// Trigger sampling pools. Volumes are tested against SWEPT points: a fast
// body in a slow frame (mobile hitch, CI) otherwise tunnels straight through
// rings, gates, kill fields and the goal without ever being inside them on a
// sampled frame.

/**
 * First-flight training: one instruction at a time, each cleared by DOING
 * it, not by waiting it out. Friends of friends walked straight into the
 * pit without knowing jump or portals existed; the fix is telling them, in
 * the vocabulary of whatever they are holding (thumbs, pad, mouse).
 */
const TUT_STEPS = [
  {
    id: 'look',
    text: {
      kb: 'MOVE THE MOUSE\nLOOK AROUND',
      mac: 'MOUSE OR TRACKPAD\nLOOK AROUND',
      touch: 'SWIPE THE RIGHT SIDE\nLOOK AROUND',
      pad: 'RIGHT STICK\nLOOK AROUND',
    },
  },
  {
    id: 'move',
    text: {
      kb: 'W A S D\nMOVE', mac: 'W A S D\nMOVE',
      touch: 'LEFT THUMB\nMOVE', pad: 'LEFT STICK\nMOVE',
    },
  },
  {
    id: 'jump',
    text: {
      kb: 'SPACE\nJUMP', mac: 'SPACE\nJUMP',
      touch: 'JUMP BUTTON\nJUMP', pad: 'A BUTTON\nJUMP',
    },
  },
  {
    id: 'air',
    text: {
      kb: 'HOLD W A S D MID-AIR\nYOU STEER WHILE FALLING',
      mac: 'HOLD W A S D MID-AIR\nYOU STEER WHILE FALLING',
      touch: 'HOLD THE STICK MID-AIR\nYOU STEER WHILE FALLING',
      pad: 'HOLD LEFT STICK MID-AIR\nYOU STEER WHILE FALLING',
    },
  },
  {
    id: 'portals',
    text: {
      kb: 'LEFT CLICK: CYAN PORTAL\nRIGHT CLICK: ORANGE PORTAL\nFIRE BOTH AT LIT PANELS',
      mac: 'CLICK: CYAN PORTAL\nTWO-FINGER CLICK: ORANGE PORTAL\nFIRE BOTH AT LIT PANELS',
      touch: 'A AND B BUTTONS: PORTALS\nFIRE BOTH AT LIT PANELS',
      pad: 'TRIGGERS: PORTALS\nFIRE BOTH AT LIT PANELS',
    },
  },
];

/**
 * Enclosed sectors put a floor where open-sky sectors have the void, so a
 * missed flight leaves the player standing on a deep floor with no way up
 * instead of rebooting them at the checkpoint. The respawn key is the way
 * out and nothing on screen says so; this says so, once per life, after
 * the player has stood still well below their checkpoint for a while.
 * The drop threshold keeps it quiet while reading a sign on the deck the
 * checkpoint sits on; the wait keeps it quiet while planning a shot.
 */
const STRANDED_SECONDS = 8;
const STRANDED_DROP = 8;

const _sweepPool = Array.from({ length: 24 }, () => new THREE.Vector3());
const _sweepList = [];
const _rsweepPool = Array.from({ length: 24 }, () => new THREE.Vector3());
const _rsweepList = [];
const _silentBus = { emit() {} };

/**
 * Feet/waist/head sample points at up to eight positions interpolated along
 * this frame's displacement. `cut` (teleport, respawn) and teleport-scale
 * jumps collapse to the end position only: sweeping a portal hop would test
 * a straight line through geometry the body never crossed.
 */
function sweptPoints(prev, ctrl, pool, list, cut) {
  const p = ctrl.position;
  const moved = cut ? 0 : prev.distanceTo(p);
  const steps = moved > 60 ? 1 : Math.min(8, Math.max(1, Math.ceil(moved / 2.2)));
  let n = 0;
  for (let i = 1; i <= steps; i++) {
    const f = i / steps;
    const x = prev.x + (p.x - prev.x) * f;
    const y = prev.y + (p.y - prev.y) * f;
    const z = prev.z + (p.z - prev.z) * f;
    list[n] = pool[n].set(x, y, z); n++;
    list[n] = pool[n].set(x, y + ctrl.height * 0.5, z); n++;
    list[n] = pool[n].set(x, y + ctrl.height, z); n++;
  }
  list.length = n;
  return list;
}
const _v = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _impact = new THREE.Vector3();
const _aim = new THREE.Vector3();

const STATE = { BOOT: 'boot', MENU: 'menu', PLAYING: 'playing', PAUSED: 'paused', RESULT: 'result' };

/** The results line for a challenge run: who won, and by how much. */
export function challengeVerdict(challenge, time) {
  const delta = Math.abs(time - challenge.time).toFixed(2);
  if (time < challenge.time) return `YOU BEAT ${challenge.name} BY ${delta}S`;
  if (time > challenge.time) return `${challenge.name} HOLDS IT BY ${delta}S`;
  return `DEAD HEAT WITH ${challenge.name}`;
}

export class Game {
  constructor(canvas) {
    this.canvas = canvas;
    this.bus = new EventBus();
    this.settings = new Settings(this.bus);
    this.save = new Save();
    this.audio = new AudioEngine(this.settings);
    this.state = STATE.BOOT;

    this.engine = new Engine({ canvas, bus: this.bus, settings: this.settings });
    this.scene = this.engine.scene;
    this.camera = this.engine.camera;
    this.world = new PhysicsWorld();

    this.level = null;
    this.mode = null;
    this.levelDef = null;
    this._deathTimer = 0;
    this._pickups = 0;
    this._flingPeak = 0;
    this._flingTimer = 0;
    this._goalPoint = null;
    this._rivalOn = false;
    this._prevPlayerPos = new THREE.Vector3();
    this._prevRivalPos = new THREE.Vector3();
    this._playerCut = true;
    this._rivalCut = true;
  }

  // -------------------------------------------------------------------- boot

  async boot(onProgress = () => {}) {
    const step = async (label, pct, fn) => {
      onProgress(pct, label);
      // rAF lets the loading bar paint, but a HIDDEN tab gets no rAF at
      // all, which used to hang boot until the tab was focused (found by
      // the agent deck's first background run; it bites any player who
      // opens the game in a background tab too). Race a timeout so boot
      // always advances; throttled background timers just mean a slower
      // boot, not a frozen one.
      await new Promise((r) => {
        const t = setTimeout(r, 150);
        requestAnimationFrame(() => { clearTimeout(t); r(); });
      });
      return fn?.();
    };

    await step('FORGING SURFACES', 12, () => {
      this.materials = new MaterialLibrary(this.engine.tier);
    });

    await step('RAISING THE SKYLINE', 34, () => {
      this.sky = new Sky(this.engine.tier);
      this.scene.add(this.sky.group);
    });

    await step('BAKING REFLECTIONS', 52, () => {
      this.envMap = this.sky.buildEnvironment(this.engine.renderer);
      this.scene.environment = this.envMap;
      this.scene.environmentIntensity = 0.95;
    });

    await step('WIRING LIGHT', 64, () => {
      const hemi = new THREE.HemisphereLight(0x5fb6ff, 0x2a1030, 1.25);
      this.scene.add(hemi);

      const key = new THREE.DirectionalLight(0xbfe9ff, 1.8);
      key.position.set(-42, 68, 30);
      key.castShadow = this.engine.tier.shadows;
      const s = this.engine.tier.shadowSize;
      key.shadow.mapSize.set(s, s);
      key.shadow.camera.near = 1;
      key.shadow.camera.far = 260;
      const span = 70;
      key.shadow.camera.left = -span;
      key.shadow.camera.right = span;
      key.shadow.camera.top = span;
      key.shadow.camera.bottom = -span;
      key.shadow.bias = -0.0012;
      key.shadow.normalBias = 0.04;
      this.scene.add(key);
      this.scene.add(key.target);
      this.keyLight = key;

      const rim = new THREE.DirectionalLight(0xff3fa0, 0.4);
      rim.position.set(50, 40, -60);
      this.scene.add(rim);
    });

    await step('OPENING THE APERTURE', 78, () => {
      this.portals = new PortalSystem({
        engine: this.engine, world: this.world, bus: this.bus, scene: this.scene,
      });
      // Resize and tier hooks only. Registering the whole system would have
      // the engine call update() a second time each frame on top of the
      // explicit call in Game.update, and the iris would animate double speed.
      this.engine.addSystem({
        resize: () => this.portals.resize(),
        onTier: (t) => this.portals.onTier(t),
      });
    });

    await step('CALIBRATING OPTICS', 88, () => {
      this.postfx = new PostFX(this.engine, this.scene, this.camera);
      this.engine.addSystem({ resize: () => this.postfx.resize(), onTier: (t) => this.postfx.onTier(t) });
    });

    await step('LINKING CONTROLS', 96, () => {
      this.input = new InputManager({ canvas: this.canvas, settings: this.settings, bus: this.bus });
      this.player = new Player({
        engine: this.engine, world: this.world, portals: this.portals,
        bus: this.bus, settings: this.settings, audio: this.audio,
      });
      this.rival = new Rival({
        world: this.world, portals: this.portals, bus: this.bus, materials: this.materials,
      });
      this.scene.add(this.rival.group);
      this.rivalAgent = new RivalAgent({ rival: this.rival, world: this.world, bus: this.bus });
      this.rivalBrain = new RivalBrain({ agent: this.rivalAgent, bus: this.bus });
      this.rivalVoice = new RivalVoice({ bus: this.bus });
      this.ghostRecorder = new GhostRecorder();
      this.ghostRider = new GhostRider(this.scene);
      this.remotes = new RemotePlayers(this.scene);
      this._marker = this._makeLandingMarker();
      this.race = null;

      this.hud = new HUD();
      this.screens = new Screens({
        save: this.save, settings: this.settings, audio: this.audio,
        on: (intent, payload) => this.onIntent(intent, payload),
      });
      this._wireEvents();
    });

    this.engine.renderHook = (dt, elapsed) => this.render(dt, elapsed);
    this.engine.addSystem({ update: (dt, elapsed) => this.update(dt, elapsed), order: -10 });
    this.engine.start();

    // Fire and forget: the rival works offline, this only decides whether it
    // also gets to think.
    this.rivalBrain.probe().then((ok) => {
      if (ok) console.info('[rival] planner online');
      // Same health signal: a configured rival endpoint means a voice too.
      this.rivalVoice.available = ok && !this._agentMode;
    });

    // A callsign exists before the first record does; PILOT-042 beats an
    // empty-name prompt at the moment of victory.
    if (!this.settings.get('handle')) {
      this.settings.set('handle', `PILOT-${String(Math.floor(Math.random() * 900) + 100)}`);
    }

    wireErrorReporting(pkg.version);
    // Meta carries device class, tier, version and install mode: the
    // adoption and perf splits on the stats page all parse this string.
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches ? ' pwa' : '';
    track('session_start', {
      meta: `${this.input.touch ? 'touch' : 'desktop'} ${this.engine.tier?.name ?? ''} v${pkg.version}${standalone}`,
    });

    onProgress(100, 'READY');
    this.state = STATE.MENU;
    this.screens.replace('title');
  }

  // ------------------------------------------------------------------ wiring

  _wireEvents() {
    const bus = this.bus;

    bus.on(EV.SETTINGS_CHANGED, ({ key, values }) => {
      if (key === 'quality' || key === '*') this.engine.applyTier(values.quality);
      if (key === 'fov' || key === '*') this.player.onFovSetting(values.fov);
      if (key === 'music' || key === 'sfx' || key === '*') this.audio.applySettings();
      if (key === 'showFps' || key === '*') this.hud.setFps(0, !!values.showFps);
      if (key === 'rival' || key === '*') this.setRivalEnabled(!!values.rival);
    });

    bus.on('input:gamepad', () => this.hud.showToast('GAMEPAD CONNECTED', false, 2.0));
    bus.on('pad:menu', ({ op }) => this.screens.padMenu(op));
    bus.on('rival:say', ({ text }) => this.hud.showTaunt(text));
    bus.on('rival:offline', ({ reason }) => {
      console.warn('[rival] planner offline, falling back to the local bot:', reason);
      this.hud.showToast('RIVAL LINK LOST');
    });

    const fsBtn = document.getElementById('btn-fs');
    fsBtn?.addEventListener('click', async () => {
      this.audio.ui('click');
      const on = await Fullscreen.toggle(this.input.touch);
      // The setting means "auto-fullscreen when a level starts", which only
      // exists on touch; on desktop the button is a transient session control
      // and writing the setting from it would flip a switch that does nothing.
      if (this.input.touch) this.settings.set('fullscreen', on ? 1 : 0);
    });
    if (!Fullscreen.supported()) fsBtn?.classList.add('nofs');
    Fullscreen.onChange((active) => fsBtn?.classList.toggle('on', active));

    bus.on(EV.TOAST, ({ text, warn }) => this.hud.showToast(text, warn));

    // Presentation belongs to the actor. Both events fire for the rival too,
    // and a full-screen warp because someone ELSE dove through a portal reads
    // as a glitch, not a game event.
    bus.on(EV.PORTAL_PLACED, ({ which, owner }) => {
      if (owner === 'player' && this._tut) this._tut.placed.add(which);
      if (owner === 'player') this.audio.portalOpen(which);
    });
    bus.on(EV.PORTAL_DENIED, () => this.audio.portalDeny());
    // A decayed portal announces itself; same actor rule as placement.
    bus.on(EV.PORTAL_CLEARED, ({ owner, decayed }) => {
      if (decayed && owner === 'player') this.audio.portalDeny();
    });
    bus.on(EV.SHEAR, () => {
      // Losing your speed to an ion beam should sting audibly and visibly.
      this.audio.portalDeny();
      this.postfx.flash(0.3);
    });
    bus.on(EV.SHATTER, ({ impact }) => {
      // The land() crunch doubles convincingly as breaking glass, scaled
      // by how hard the panel was hit.
      this.audio.land(Math.min(60, impact));
      this.postfx.flash(0.25);
    });
    bus.on(EV.PLAYER_TELEPORT, ({ id }) => {
      if (id === 'player') this._playerCut = true;
      if (id === 'rival') this._rivalCut = true;
      if (id !== 'player') return;
      this.postfx.warp(1);
      this._flingTimer = 0.9;
      this._flingPeak = Math.max(this._flingPeak, this.player.speed);
    });
    bus.on(EV.PLAYER_BOOST, () => { this.audio.boost(); this.postfx.flash(0.18); });
    bus.on(EV.CHECKPOINT, () => {
      this.audio.checkpoint();
      this.hud.showToast('CHECKPOINT');
    });
    bus.on(EV.PICKUP, ({ position }) => {
      this.audio.pickup(this._pickups++);
      this.postfx.flash(0.12);
      if (position) this.rivalAgent.markShardTaken(_v.fromArray(position));
    });

    bus.on(EV.LEVEL_COMPLETE, ({ time }) => this.onLevelComplete(time));
    bus.on(EV.LEVEL_FAILED, ({ reason }) => this.onLevelFailed(reason));

    // Pointer-lock loss IS the Escape key (the browser eats the keydown
    // while locked); treat it as the pause gesture.
    // The lock may only be held while PLAYING: a request that resolves
    // after a pause snuck in (menus up, mouse captured, no cursor: the
    // "stuck on resume" report) is released on the spot.
    bus.on('input:gotLock', () => {
      if (this.state !== STATE.PLAYING) this.input.releaseLock();
    });
    bus.on('input:lostLock', () => {
      if (this.state !== STATE.PLAYING || this.input.touch) return;
      // Escape means "give me the menu": players press it, see the mouse
      // freed, and expect clickable items, not a toast (the Escape-Escape
      // dance was reported as plain confusing). The old mid-fling ambush
      // came from spurious raw-input drops on Windows; the adaptive bailout
      // caps those at two per machine before switching to the stable lock,
      // which is a price worth paying for the expected behavior everywhere.
      this.pause();
    });
    // A real tab switch pauses; a click that merely lands on another
    // window or monitor does NOT (that was an ambush: the freed cursor
    // roams invisibly during unlocked play, and any stray click paused).
    document.addEventListener('visibilitychange', () => {
      // Never auto-pause a racing client: two windows on one desk occlude
      // each other, and a paused racer stops broadcasting mid-race.
      if (document.visibilityState === 'hidden' && !this.race
        && this.state === STATE.PLAYING && !this.input.touch) this.pause();
    });

    // The Android back gesture is a TWA's only navigation, and with no
    // handler it quits the app mid-run. Each level start and menu push
    // leaves a history entry to spend: back pauses a run, backs out of a
    // menu, and only exits once the player is at the title with nothing
    // left to unwind.
    window.addEventListener('popstate', () => {
      if (this.state === STATE.PLAYING) {
        this.pause();
        history.pushState({ m: 'game' }, '');
      } else if (this.screens.current && this.screens.current !== 'title') {
        document.querySelector('.screen.on [data-back]')?.click();
        if (this.screens.current !== 'title') history.pushState({ m: 'menu' }, '');
      }
    });

    // Mid-race quick-pings on 1-4; the server owns the vocabulary and the
    // cooldown, so mashing costs nothing.
    document.addEventListener('keydown', (e) => {
      if (!this.race?.open || this.state !== STATE.PLAYING) return;
      const k = { Digit1: 'gg', Digit2: 'gl', Digit3: 'wow', Digit4: 'go' }[e.code];
      if (k) this.race.sendPing(k);
    });
  }

  onIntent(intent, payload) {
    switch (intent) {
      case 'play': {
        const id = payload ?? this._firstUncleared();
        // A clean session gets one shot at naming itself up front; the
        // settings field stays the editor afterwards.
        if (!this.settings.get('handle') && !this.save.data.askedName) {
          this.save.data.askedName = 1;
          this.save.persist();
          this.screens.promptCallsign(() => this.startLevel(id));
        } else {
          this.startLevel(id);
        }
        break;
      }
      case 'levels': this.exitToMenu('levels'); break;
      case 'quit': this.exitToMenu('title'); break;
      case 'feedback': this.sendFeedback(payload); break;
      case 'resume': this.resume(); break;
      case 'pause': this.pause(); break;
      case 'daily': this.startDaily(); break;
      case 'gauntlet': this.startGauntlet(); break;
      case 'marathon': this.startMarathon(); break;
      case 'race-create': this._raceConnect(this._randomRoomCode()); break;
      case 'race-join': this._raceConnect(String(payload ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)); break;
      case 'race-start': this.race?.sendStart(); break;
      case 'race-vote': this.race?.sendVote(String(payload ?? '')); break;
      case 'race-leave': this._raceDisconnect(); break;
      case 'race-lobby': this.screens.show('race'); break;
      case 'race-rematch': this.race?.sendRematch(); break;
      case 'race-spectate': this.startSpectate(); break;
      case 'race-ping': this.race?.sendPing(String(payload ?? '')); break;
      case 'sync-push': this.syncPush(); break;
      case 'sync-pull': this.syncPull(String(payload ?? '')); break;
      case 'race-name': this.race?.sendRename(String(payload ?? '')); break;
      case 'key-rebind': {
        const { action, done } = payload ?? {};
        this.input.captureNextKey((code) => {
          if (code) {
            const binds = { ...JSON.parse(this.settings.get('keyBinds') || '{}'), [action]: code };
            this.settings.set('keyBinds', JSON.stringify(binds));
          }
          done?.(code);
        });
        break;
      }
      case 'pad-rebind': {
        const { action, done } = payload ?? {};
        this.input.captureNextButton((index) => {
          const binds = { ...JSON.parse(this.settings.get('padBinds') || '{}'), [action]: [index] };
          this.settings.set('padBinds', JSON.stringify(binds));
          done?.();
        });
        break;
      }
      // A daily restart is still the daily; a gauntlet restart is a fresh
      // run at the same day's tower.
      case 'restart':
        if (this._mutator) this.startDaily();
        else if (this.levelDef?.id === 'gauntlet') this.startGauntlet();
        else if (this._redline) this.startRedline(this.levelDef?.id);
        else this.startLevel(this.levelDef?.id);
        break;
      case 'respawn':
        // The dead screen only appears when the MODE has failed (the
        // gauntlet clock ran out). Resuming into a finished mode leaves a
        // dead timer stuck at 0:00 forever; the only meaningful reboot is
        // a fresh attempt.
        if (this.mode?.finished) this.startLevel(this.levelDef?.id);
        else { this.resume(); this.respawnPlayer(); }
        break;
      case 'next': {
        const n = this.levelDef ? nextLevel(this.levelDef.id) : null;
        this.startLevel(n ? n.id : this.levelDef?.id);
        break;
      }
      case 'toast': this.hud.showToast(String(payload)); break;
      case 'share': this.shareRun(); break;
      case 'redline': this.startRedline(String(payload ?? '')); break;
      case 'challenge-go': this._startChallenge(); break;
      default: console.warn('[game] unhandled intent', intent);
    }
  }

  _firstUncleared() {
    const lv = LEVELS.find((l) => !this.save.isCleared(l.id));
    return (lv ?? LEVELS[0]).id;
  }

  // ------------------------------------------------------------------ level

  /** The whole campaign on one clock: deaths cost time, never progress.
   *  Sector times are banked at each gate; the results screen waits for
   *  the last one, and the total goes to the marathon board (ghostless:
   *  a 15-minute track would blow every plausibility bound). */
  startMarathon() {
    this._marathon = { idx: 0, total: 0 };
    this._pendingMarathon = true;
    this.startLevel(LEVELS[0].id);
    this.hud.showToast('MARATHON\nTHE WHOLE GRID, ONE CLOCK', false, 3.2);
  }

  /** Today's combo, its own board, everyone races the same thing. */
  startDaily() {
    const { mutator, sector, board } = dailyCombo();
    this._pendingDaily = { mutator, board };
    this.startLevel(sector);
    this.hud.showToast(`DAILY RUN\n${this.levelDef.name} // ${mutator.name}`, false, 3.2);
  }

  _raceAllDone() {
    const others = (this._raceRosterIds ?? []).filter((id) => id !== this.race?.id);
    return others.length > 0 && others.every((id) => this._raceFinished?.has(id));
  }

  /** The race is over or there is nothing to watch: back to the lobby. */
  _exitToRoom(toast = null) {
    this._spectating = null;
    this.player.controller.frozen = false;
    this.state = STATE.RESULT;
    this.engine.paused = true;
    this.input.setEnabled(false);
    if (toast) this.hud.showToast(toast, false, 2.4);
    this.screens.show('race');
  }

  /** Watch the racers still out there: follow-cam, fire cycles targets. */
  startSpectate() {
    if (!this.race) return;
    // Nothing left to spectate at all? Straight to the room.
    if (this._raceAllDone()) { this._exitToRoom(); return; }
    this._spectateEmpty = 0;
    this._spectating = { idx: 0, pov: false };
    this.state = STATE.PLAYING;
    this.engine.paused = false;
    this.screens.hide();
    this.input.setEnabled(true);
    this.player.controller.frozen = true;
  }

  _spectateTargets() {
    return [...this.remotes.map.entries()].filter(([, e]) => e.shown);
  }

  _updateSpectate(dt = 1 / 60) {
    const targets = this._spectateTargets();
    if (!targets.length) {
      // A few seconds of empty course (everyone left, or worlds diverged)
      // sends the spectator home instead of hovering forever.
      this._spectateEmpty = (this._spectateEmpty ?? 0) + dt;
      if (this._spectateEmpty > 4) { this._exitToRoom('NOBODY LEFT TO WATCH'); return; }
      this.hud.setObjective('SPECTATING  //  WAITING FOR RACERS');
      return;
    }
    this._spectateEmpty = 0;
    if (this._spectateCycle) {
      this._spectateCycle = false;
      this._spectating.idx = (this._spectating.idx + 1) % targets.length;
    }
    const [, e] = targets[this._spectating.idx % targets.length];
    const yaw = e.group.rotation.y;
    if (this._spectating.pov) {
      // Their eyes: camera at the replica's visor, aimed by the relayed
      // yaw and (smoothed) pitch. The body would fill the near plane, so
      // this frame it is not drawn; the update pass re-decides next frame.
      this.camera.position.set(
        e.group.position.x, e.group.position.y + 1.52, e.group.position.z,
      );
      this.camera.rotation.set(e.pitchS, yaw, 0, 'YXZ');
      e.group.visible = false;
      this.hud.setObjective('SPECTATING POV  //  FIRE = NEXT  //  JUMP = CHASE CAM');
    } else {
      // Chase cam: behind and above the replica, looking at its head.
      this.camera.position.set(
        e.group.position.x + Math.sin(yaw) * 6,
        e.group.position.y + 3.2,
        e.group.position.z + Math.cos(yaw) * 6,
      );
      this.camera.lookAt(e.group.position.x, e.group.position.y + 1.4, e.group.position.z);
      this.hud.setObjective('SPECTATING  //  FIRE = NEXT  //  JUMP = POV');
    }
  }

  // ------------------------------------------------------------------ sync

  /**
   * Upload this device's progress under its sync code, minting one on
   * first use. The code is the credential: it lives in settings, and
   * losing it only costs the link, never the local save.
   */
  async syncPush() {
    let code = this.settings.get('syncCode');
    if (!normalizeSyncCode(code)) {
      code = makeSyncCode();
      this.settings.set('syncCode', code);
    }
    this.screens.setSyncStatus('UPLOADING...');
    try {
      // Merge with whatever the code already holds before writing: a push
      // from a device that is behind (or freshly wiped) must never blank
      // the copy another device uploaded.
      const existing = await fetch(`/api/sync?code=${code}`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      const merged = existing?.blob?.save
        ? mergeSaves(this.save.data, existing.blob.save)
        : this.save.data;
      const body = JSON.stringify(packPayload(merged, this.settings.values));
      const res = await fetch(`/api/sync?code=${code}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body,
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.reason ?? res.status);
      this.settings.set('syncAt', Date.now());
      this.screens.setSyncStatus(`SAVED TO ${formatSyncCode(code)}`);
      track('sync_push');
    } catch (err) {
      this.screens.setSyncStatus(`UPLOAD FAILED (${err.message})`, true);
    }
  }

  /**
   * Pull another device's blob and MERGE it in (union-and-best): pulling
   * can never cost you a record you set here, which is what makes the
   * button safe to press without a warning dialog.
   */
  async syncPull(input) {
    const code = normalizeSyncCode(input || this.settings.get('syncCode'));
    if (!code) { this.screens.setSyncStatus('THAT CODE IS NOT VALID', true); return; }
    this.screens.setSyncStatus('FETCHING...');
    try {
      const res = await fetch(`/api/sync?code=${code}`);
      const out = await res.json().catch(() => ({}));
      if (res.status === 404) throw new Error('no progress under that code');
      if (!res.ok || !out.ok) throw new Error(out.reason ?? res.status);
      const before = Object.keys(this.save.data.best ?? {}).length;
      this.save.data = mergeSaves(this.save.data, out.blob.save);
      this.save.persist();
      const merged = mergeSettings(this.settings.values, out.blob.settings);
      if (merged.handle !== this.settings.get('handle')) {
        this.settings.set('handle', merged.handle);
      }
      this.settings.set('syncCode', code);
      this.settings.set('syncAt', Date.now());
      const after = Object.keys(this.save.data.best ?? {}).length;
      this.screens.buildLevelGrid();
      this.screens.setSyncStatus(`MERGED  //  ${after} SECTORS (${after - before} NEW)`);
      track('sync_pull');
    } catch (err) {
      this.screens.setSyncStatus(`PULL FAILED (${err.message})`, true);
    }
  }

  _randomRoomCode() {
    const abc = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 5; i++) code += abc[Math.floor(Math.random() * abc.length)];
    return code;
  }

  _raceConnect(code) {
    if (!code || code.length < 4) { this.hud.showToast('BAD ROOM CODE', true); return; }
    this._raceDisconnect();
    this.race = new RaceClient({
      code,
      name: this.settings.get('handle'),
      on: (m) => this._onRaceMessage(m),
    });
    this.screens.setRaceStatus({ code, roster: [], isHost: false, connected: false });
  }

  _raceDisconnect() {
    this.race?.dispose();
    this.race = null;
    this.remotes.clear();
    this._raceGateUntil = 0;
    this.screens.setRaceHistory([]);
  }

  _onRaceMessage(m) {
    switch (m.t) {
      case 'hello':
        // A race already underway? Note it; the roster that follows decides.
        if (m.race?.level) this._raceInProgress = m.race;
        break;
      case 'roster': {
        const roster = m.players ?? [];
        this._raceRosterIds = roster.map((p) => p.id);
        for (const p of roster) {
          if (p.id === this.race.id) continue;
          const known = this.remotes.map.get(p.id);
          if (known && known.name !== p.name) this.remotes.setName(p.id, p.name);
          else this.remotes.ensure(p.id, p.name);
        }
        this.screens.setRaceStatus({
          code: this.race.code, roster, isHost: this.race.isHost, connected: true,
        });
        // Mid-race join: someone is still out there racing; watch them.
        const pending = this._raceInProgress;
        this._raceInProgress = null;
        if (pending && Date.now() - pending.at < 10 * 60 * 1000
          && roster.some((p) => p.id !== this.race.id && p.finished === null)) {
          this.startLevel(pending.level);
          this.startSpectate();
          this.hud.showToast('RACE IN PROGRESS\nSPECTATING', false, 3.0);
        }
        break;
      }
      case 's':
        if (m.id === this.race?.id) break;
        // Different sector, different world: a racer who moved on must not
        // haunt this one with their coordinates.
        if (m.l && m.l !== this.levelDef?.id) this.remotes.hide(m.id);
        else this.remotes.setState(m.id, m.p, m.y, m.v, m.x);
        break;
      case 'p':
        if (m.id === this.race?.id) break;
        if (!m.l || m.l === this.levelDef?.id) this.remotes.setPortal(m.id, m.w, m.p, m.q);
        break;
      case 'pc':
        this.remotes.clearPortals(m.id);
        break;
      case 'start': {
        this._raceFinished = new Set();
        // Shared clock: the room said "go at T"; freeze until then.
        this._raceGateUntil = performance.now() + Math.max(0, m.at - Date.now());
        this.startLevel(m.level);
        // Grid slots behind the spawn: everyone computes the same order, so
        // nobody's camera opens inside a teammate (the start white-out).
        const ids = [...(this._raceRosterIds ?? [])].sort();
        const slot = Math.max(0, ids.indexOf(this.race.id));
        const { dx, dz } = startOffset(slot);
        const sp = this.level.spawn.position;
        this.player.controller.teleportTo(sp.x + dx, sp.y, sp.z + dz);
        this.hud.showToast('RACE INCOMING', false, 1.6);
        break;
      }
      case 'fin':
        (this._raceFinished ??= new Set()).add(m.id);
        this.hud.showToast(`${m.name} FINISHED P${m.place}\n${m.time.toFixed(2)}s`, false, 3.0);
        // The last racer just crossed: spectators return to the room.
        if (this._spectating && this._raceAllDone()) this._exitToRoom('RACE COMPLETE');
        break;
      case 'ping':
        // The feed, not the HUD toast: menus sit over the HUD layer, and
        // the lobby is exactly where pings land (reported as "not sent").
        this.screens.showPing(`${m.name}: ${PINGS[m.k] ?? m.k}`);
        this.audio.ui?.('click');
        break;
      case 'history':
        this.screens.setRaceHistory(m.races ?? []);
        break;
      case 'bye':
        this.remotes.remove(m.id);
        break;
      case 'closed':
        if (this.race) {
          this.hud.showToast(m.early ? 'COULD NOT JOIN\nROOM FULL OR BAD CODE' : 'ROOM CLOSED', true);
        }
        this._raceDisconnect();
        this.screens.setRaceStatus(null);
        break;
      default:
        break;
    }
  }

  /** The day's tower: same tower for everyone, one life, depth board. */
  startGauntlet() {
    const { def, board } = generateGauntlet();
    this._gauntletDef = def;
    this._pendingGauntlet = board;
    this.startLevel('gauntlet');
  }

  startLevel(id) {
    this._pushHistory('game');
    // A normal start must never inherit theater state (quit-to-menu from a
    // pause inside the theater leaves it set; _watchRemote re-sets it
    // right after this returns).
    this._clearTheater();
    // A pending redline swaps in the derived def for this one start; any
    // other start (NEXT, a board race, a daily) is a normal run again.
    const redline = this._pendingRedline?.id === id ? this._pendingRedline : null;
    this._pendingRedline = null;
    this._redline = redline ? { seconds: redline.def.mode.redline } : null;
    const def = id === 'gauntlet' && this._gauntletDef
      ? this._gauntletDef
      : (redline?.def ?? LEVELS_BY_ID[id] ?? LEVELS[0]);
    this.unloadLevel();
    this.levelDef = def;
    // Fresh death ledger; unloadLevel just reported the previous run's.
    this._runLive = true;
    this._runDeaths = 0;
    this._runCleared = false;
    this._stranded = null;

    const ctx = {
      scene: this.scene,
      world: this.world,
      materials: this.materials,
      bus: this.bus,
      tier: this.engine.tier,
    };
    this.level = buildLevel(def, ctx);
    this.mode = createMode(this.level, { bus: this.bus, player: this.player });

    this.applyEnv(def.env);

    this.portals.reset();
    this._pickups = 0;
    this._flingPeak = 0;
    this.player.controller.resetStats();
    this.player.spawn(this.level.spawn.position, this.level.spawn.yaw);
    this.keyLight.position.set(
      this.level.spawn.position.x - 42, this.level.spawn.position.y + 68, this.level.spawn.position.z + 30);
    this.keyLight.target.position.copy(this.level.spawn.position);

    this._goalPoint = this.level.byRole.goal[0]?.triggers?.[0]?.center?.clone() ?? null;
    // Mutator lifecycle: restore whatever the last run patched, then apply
    // today's, to player and rival both, so the race stays fair.
    this._restoreMutator?.();
    this._restoreMutator = null;
    this._mutator = null;
    // Level-authored physics: absolute overrides from the def, applied
    // BEFORE any daily mutator so multipliers stack on the level's numbers.
    this._restorePhysics?.();
    this._restorePhysics = null;
    if (def.physics) {
      this._restorePhysics = applyPhysics(def.physics,
        [this.player.controller, this.rival.controller]);
    }
    this._runKey = redline ? redlineKey(id) : id;
    if (this.race) this.remotes.hideAll();
    this._spectating = null;
    this.player.controller.frozen = false;
    const gauntletBoard = this._pendingGauntlet;
    this._pendingGauntlet = null;
    if (gauntletBoard) this._runKey = gauntletBoard;
    const daily = this._pendingDaily;
    this._pendingDaily = null;
    if (daily) {
      this._mutator = daily.mutator;
      this._runKey = daily.board;
      this._restoreMutator = applyMutator(daily.mutator, [this.player.controller, this.rival.controller]);
    }
    // Decaying portals: a level rule (def.portalDecay) or a mutator that
    // carries one; anything else runs the classic forever-portals.
    this.portals.setDecay(def.portalDecay ?? this._mutator?.portalDecay ?? null);
    // Marathon lifecycle: the chain re-arms the flag before every hop; a
    // start that did not is the player leaving the marathon.
    const marathonHop = this._pendingMarathon;
    this._pendingMarathon = false;
    if (!marathonHop) this._marathon = null;
    if (this._marathon) this._runKey = 'marathon';
    this.audio.setVibe(def);
    track('level_start', { level: id, meta: this._redline ? 'redline' : (this._mutator?.id ?? '') });
    this._playerCut = true;
    this._rivalCut = true;
    this._startRival();

    // Every run is recorded; the recording only survives if it sets a record.
    this.ghostRecorder.reset();
    this.ghostRider.stop();
    // A challenge outlives one attempt (RUN IT AGAIN keeps racing the
    // same ghost) and dies the moment a different sector starts.
    if (this._challenge && this._challenge.level !== id) this._challenge = null;
    const remote = this._remoteGhost ?? this._challenge;
    this._remoteGhost = null;
    if (remote && remote.level === id && this.ghostRider.start(remote.track)) {
      this.hud.showToast(`RACING ${remote.name}'S ${remote.track.time.toFixed(1)}s GHOST`);
    } else if (this.settings.get('ghost')) {
      const track = this.save.ghost(this._runKey);
      if (track && this.ghostRider.start(track)) {
        this.hud.showToast(`RACING YOUR ${track.time.toFixed(1)}s GHOST`);
      }
    }

    this.mode.start();
    this.hud.setLevel(def.name, this.mode.hud().objective ?? '');
    this.hud.setShards(0, this.level.shardTotal);
    this.hud.setPortals(false, false);
    this.hud.show(true);
    this.hud.showToast(def.name, false, 2.4);

    this.screens.hide();
    this.state = STATE.PLAYING;
    this.engine.paused = false;
    this.input.setEnabled(true);
    if (!this.input.touch) this.input.requestLock();
    document.getElementById('touch')?.classList.toggle('hidden', !this.input.touch);
    document.getElementById('touch')?.classList.toggle('on', this.input.touch);
    document.getElementById('btn-pause')?.classList.remove('hidden');

    // Requesting fullscreen only works inside the gesture that started the
    // level, which is exactly where this runs. Touch only: on a phone the
    // browser chrome eats a third of a small screen, but a desktop player
    // asked to play in a window and forcing them out of it is rude. The FS
    // button stays available on both.
    if (this.input.touch && this.settings.get('fullscreen')
        && Fullscreen.supported() && !Fullscreen.isActive()) {
      Fullscreen.request(true);
    }
    document.getElementById('btn-fs')?.classList.toggle('hidden', !Fullscreen.supported());

    this.audio.unlock().then(() => this.audio.startMusic());
    this.bus.emit(EV.LEVEL_START, { def });
  }

  /** Per-sector presentation: fog, sky palette, void floor. The editor
   *  drives this too, so a def previews exactly as it will play. */
  applyEnv(env) {
    env = env ?? {};
    this.scene.fog = new THREE.FogExp2(env.fog ?? 0x140b26, env.fogDensity ?? 0.0038);
    if (env.sky) {
      const u = this.sky.domeMat.uniforms;
      u.uZenith.value.setHex(env.sky.zenith);
      u.uHorizon.value.setHex(env.sky.horizon);
      u.uGlow.value.setHex(env.sky.glow);
      u.uAurora.value.setHex(env.sky.aurora);
    }
    this.sky.setFloorY(env.floorY ?? -320);
  }

  unloadLevel() {
    // The run's death ledger, reported exactly once per run at whatever
    // end it met. Deaths were invisible to telemetry (killPlayer is a soft
    // respawn, level_fail only fires on mode timeouts), which made every
    // sector look painless; this row is the difficulty signal. The
    // cleared/abandoned split separates "hard" from "boring": abandoning
    // with five deaths is a wall, abandoning with none is a shrug.
    if (this._runLive && this.levelDef) {
      this._runLive = false;
      track('run_deaths', {
        level: this.levelDef.id,
        value: this._runDeaths ?? 0,
        meta: this._runCleared ? 'cleared' : 'abandoned',
      });
    }
    this.mode?.dispose();
    this.level?.dispose();
    this.portals.reset();
    this.mode = null;
    this.level = null;
  }

  exitToMenu(screen = 'title') {
    this._clearTheater();
    this._challenge = null;
    this._redline = null;
    this._pendingRedline = null;
    this.unloadLevel();
    this.audio.setAmbience({ speed: 0, active: false });
    this.levelDef = null;
    this.state = STATE.MENU;
    this.engine.paused = false;
    this.input.setEnabled(false);
    this.hud.show(false);
    this.rival?.setEnabled(false);
    this.rivalBrain?.abort();
    document.getElementById('touch')?.classList.add('hidden');
    document.getElementById('btn-pause')?.classList.add('hidden');
    document.getElementById('btn-fs')?.classList.add('hidden');
    this.screens.replace(screen);
  }

  _deviceKind() {
    if (this.input.touch) return 'touch';
    if (this.input.padActive) return 'pad';
    return /mac/i.test(navigator.platform ?? navigator.userAgent) ? 'mac' : 'kb';
  }

  _updateTutorial(dt, input) {
    if (this._spectating) { this.hud.setHint(null); return; }
    // Veterans from before this shipped have nothing to learn: a save with
    // real completions graduates on sight instead of being lectured.
    if (!this._tut && this.save.data.runs > 3) {
      this.save.data.tut = 1;
      this.save.persist();
      return;
    }
    const t = (this._tut ??= { step: 0, acc: 0, placed: new Set() });
    const step = TUT_STEPS[t.step];
    if (!step) return;
    this.hud.setHint(step.text[this._deviceKind()] ?? step.text.kb);

    const wish = Math.abs(input.moveX) + Math.abs(input.moveZ);
    let done;
    switch (step.id) {
      case 'look':
        t.acc += Math.abs(input.lookX) + Math.abs(input.lookY);
        done = t.acc > 0.9;
        break;
      case 'move':
        if (wish > 0.1) t.acc += dt;
        done = t.acc > 1.0;
        break;
      case 'jump':
        done = input.jumpPressed;
        break;
      case 'air':
        if (!this.player.controller.grounded && wish > 0.1) t.acc += dt;
        done = t.acc > 0.5;
        break;
      case 'portals':
        done = t.placed.has('a') && t.placed.has('b');
        break;
      default:
        done = true;
    }
    if (!done) return;
    t.step += 1;
    t.acc = 0;
    this.audio.ui?.('click');
    if (t.step >= TUT_STEPS.length) {
      this.hud.setHint(null);
      this.hud.showToast('FLIGHT TRAINING COMPLETE', false, 2.6);
      this.save.data.tut = 1;
      this.save.persist();
      this._tut = null;
    }
  }

  _updateStranded(dt) {
    const s = (this._stranded ??= { still: 0, shown: false, spent: false });
    const c = this.player.controller;
    const stuck = !this._spectating && c.grounded && c.velocity.lengthSq() < 0.25
      && c.position.y < this.player.checkpoint.position.y - STRANDED_DROP;
    if (!stuck) {
      s.still = 0;
      if (s.shown) { this.hud.setHint(null); s.shown = false; }
      return;
    }
    s.still += dt;
    if (s.spent || s.still < STRANDED_SECONDS) return;
    s.shown = s.spent = true;
    this.hud.setHint(`${this.input.respawnLabel(this._deviceKind())}: REBOOT TO CHECKPOINT`);
  }

  /** One spendable history entry per depth, for the Android back gesture. */
  _pushHistory(mark) {
    try { history.pushState({ m: mark }, ''); } catch { /* file:// and the like */ }
  }

  pause() {
    this.screens.setPauseRoomVisible(!!this.race?.open);
    if (this.state !== STATE.PLAYING) return;
    // Menus need a cursor; a pause that leaves the pointer captured is a
    // trap with no visible mouse and no keyboard focus.
    this.input.releaseLock();
    this.state = STATE.PAUSED;
    this.audio.setAmbience({ speed: 0, active: false });
    this.engine.paused = true;
    this.input.setEnabled(false);
    this.screens.replace('pause');
  }

  resume() {
    if (this.state !== STATE.PAUSED && this.state !== STATE.RESULT) return;
    this.state = STATE.PLAYING;
    this.engine.paused = false;
    this.screens.hide();
    this.input.setEnabled(true);
    if (!this.input.touch) this.input.requestLock();
  }

  onLevelComplete(time) {
    if (this.state === STATE.RESULT) return;
    this._runCleared = true;
    if (this._rivalOn && this.rivalVoice && !this.rival.finished) {
      this.rivalVoice.say('player_won', {
        level: this.levelDef?.name, raceSeconds: time,
      }, { once: true });
    }

    // Marathon hop: bank and chain WITHOUT leaving PLAYING. The pointer
    // lock survives a level transition only if nothing releases it, and
    // the result-screen head below drops it via input.setEnabled(false);
    // re-acquiring needs a user gesture the goal trigger does not have
    // (field report: "the browser loses the mouse between rounds"). The
    // FINAL gate falls through to the normal results flow instead.
    if (this._marathon && (this.mode?.depth ?? null) == null) {
      const next = nextLevel(this.levelDef.id);
      if (next) {
        this._marathon.total += time;
        this._marathon.idx += 1;
        const banked = this._marathon.total;
        track('level_complete', {
          level: this.levelDef.id, value: +time.toFixed(2), meta: this._mutator?.id ?? '',
        });
        this.audio.victory();
        this.postfx.flash(0.4);
        this._pendingMarathon = true;
        this.startLevel(next.id);
        this.hud.showToast(`BANKED ${banked.toFixed(1)}S\n${next.name}`, false, 2.6);
        return;
      }
    }

    this.state = STATE.RESULT;
    this.engine.paused = true;
    this.input.setEnabled(false);
    this.audio.setAmbience({ speed: 0, active: false });
    this.audio.victory();
    this.postfx.flash(0.6);

    const stats = this.player.controller.stats;
    if (this.race?.open) this.race.sendFinish(time);
    const depth = this.mode?.depth ?? null;
    // Every run is recorded; this run's track is what a record stores and
    // what CHALLENGE A FRIEND sends, record or not.
    const run = this.ghostRecorder.track(time);
    const challenge = this._challenge?.level === this.levelDef.id ? this._challenge : null;
    track('level_complete', {
      level: this.levelDef.id,
      value: +time.toFixed(2),
      meta: depth != null ? `depth${depth}` : challenge ? 'challenge'
        : this._redline ? 'redline' : (this._mutator?.id ?? ''),
    });
    // Marathon final gate (hops returned above): total, board, results.
    if (this._marathon && depth == null) {
      this._marathon.total += time;
      const total = +this._marathon.total.toFixed(2);
      this._marathon = null;
      const isMRecord = this.save.recordRun('marathon', total, stats);
      this._submitMarathon(total);
      this._showBoard('marathon');
      this.screens.showResults({
        level: { id: 'marathon', name: 'MARATHON', mode: {} },
        time: total,
        best: this.save.bestTime('marathon'),
        isRecord: isMRecord,
        medal: 'none',
        topSpeed: stats.topSpeed,
        maxDrop: stats.maxDrop,
        hasNext: false,
        inRace: false,
        raceHost: false,
      });
      return;
    }
    let isRecord;
    if (depth != null) {
      // Tower records are depth records; deeper beats longer.
      if (!this.save.data.tower) this.save.data.tower = {};
      isRecord = depth > (this.save.data.tower[this._runKey] ?? 0);
      if (isRecord) { this.save.data.tower[this._runKey] = depth; this.save.persist(); }
      this.hud.showToast(`RUN OVER\nDEPTH ${depth}`, false, 3.0);
    } else {
      isRecord = this.save.recordRun(this._runKey, time, stats);
    }
    // A record run becomes the ghost to beat next time, and the world hears
    // about it. Fire and forget: the leaderboard is a bonus, never a gate.
    if (isRecord) {
      this.save.setGhost(this._runKey, run);
      this._submitScore(this._runKey, time, run, depth != null ? { kind: 'depth', depth } : {});
    }
    // Challenge links carry a plain campaign run: a daily's mutator and a
    // gauntlet's tower are not in the link, and a race room has its own
    // social loop.
    const inRace = !!this.race?.open;
    const shareable = !inRace && depth == null && !this._agentMode
      && !!LEVELS_BY_ID[this.levelDef.id] && this._runKey === this.levelDef.id;
    this._lastRun = shareable ? { level: this.levelDef.id, time, track: run } : null;
    this._showBoard(this._runKey);
    this.screens.showResults({
      level: this.levelDef,
      time,
      best: this.save.bestTime(this._redline ? this._runKey : this.levelDef.id),
      isRecord,
      medal: this.mode.medal(),
      topSpeed: stats.topSpeed,
      maxDrop: stats.maxDrop,
      hasNext: !this._redline && !!nextLevel(this.levelDef.id),
      inRace,
      raceHost: !!this.race?.isHost,
      shareable,
      verdict: challenge ? challengeVerdict(challenge, time) : null,
    });
  }

  // ------------------------------------------------------------- redline
  // The sector past its safe limit (docs/REDLINE.md): the derived def
  // rides one startLevel through _pendingRedline, and the run keys under
  // r-<id> so records, ghosts and boards never touch the base sector's.

  startRedline(id) {
    const base = LEVELS_BY_ID[id];
    if (!base || !hasRedline(base)) { this.hud.showToast('NO REDLINE HERE', true); return; }
    this._marathon = null;
    this._pendingDaily = null;
    this._pendingGauntlet = null;
    this._pendingRedline = { id, def: redlineDef(base) };
    this.startLevel(id);
  }

  // ---------------------------------------------------------- challenges
  // /r/<id> links (worker/share.js). The link opens a landing screen
  // rather than the sector: its button is the user gesture that pointer
  // lock, fullscreen and audio all need, and a new player deserves one
  // line of context before the pit.

  async openChallenge(id) {
    let body = null;
    try {
      const res = await fetch(`/api/share?id=${encodeURIComponent(id)}`);
      body = await res.json().catch(() => null);
    } catch { /* offline: handled below */ }
    const def = body?.ok ? LEVELS_BY_ID[body.level] : null;
    if (!def || !body.track) {
      this.hud.showToast(body?.reason === 'not_found' ? 'CHALLENGE EXPIRED' : 'CHALLENGE UNAVAILABLE', true, 3.5);
      return false;
    }
    this._challenge = { id, level: def.id, name: body.name, time: body.time, track: body.track };
    track('share_open', { level: def.id });
    this.screens.showChallenge({ name: body.name, levelName: def.name, time: body.time });
    return true;
  }

  _startChallenge() {
    const c = this._challenge;
    if (!c) { this.exitToMenu('title'); return; }
    this._marathon = null;
    this._pendingDaily = null;
    this._pendingGauntlet = null;
    this.startLevel(c.level);
  }

  /** CHALLENGE A FRIEND: store the run just finished, hand the link to the
   *  share sheet or the clipboard, and say so on the button. */
  async shareRun() {
    const run = this._lastRun;
    if (!run) return;
    if (!this.settings.get('handle')) {
      // A challenge needs a name on it; the first share is the moment to ask.
      this.screens.promptCallsign(() => { this.screens.replace('results'); this.shareRun(); });
      return;
    }
    this.screens.setShareState('busy');
    let body = null;
    try {
      const res = await fetch('/api/share', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          level: run.level, name: this.settings.get('handle'), time: run.time, ghost: run.track,
        }),
      });
      body = await res.json().catch(() => null);
    } catch { /* offline: reported below */ }
    if (!body?.ok || !body.url) { this.screens.setShareState('failed'); return; }
    const levelName = LEVELS_BY_ID[run.level]?.name ?? run.level.toUpperCase();
    const how = await deliverLink({
      title: 'MOMENTUM',
      text: shareText({ name: this.settings.get('handle'), levelName, time: run.time }),
      url: body.url,
    });
    this.screens.setShareState(how ?? 'idle');
    // No share sheet and no clipboard (an old browser, a denied
    // permission): the link itself is the fallback, on screen.
    if (!how) this.hud.showToast(body.url, false, 8);
  }

  async _submitScore(level, time, ghost, extra = {}) {
    if (this._agentMode) return;   // agent sessions never touch boards
    try {
      const res = await fetch('/api/scores', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ level, name: this.settings.get('handle'), time, ghost, ...extra }),
      });
      const body = await res.json().catch(() => null);
      if (body?.ok && body.improved && body.rank) {
        this.hud.showToast(`WORLD RANK #${body.rank}`);
        this._showBoard(level, body.board);
      } else if (body && body.ok === false) {
        // A refusal is not a network problem: the run is saved, the board
        // has no row, and the player deserves the reason (audit finding 2).
        this.hud.showToast(boardRefusalText(body.reason), true, 4.5);
      }
    } catch { /* offline is fine; the run is already saved locally */ }
  }

  async _submitMarathon(time) {
    if (this._agentMode) return;   // agent sessions never touch boards
    try {
      const res = await fetch('/api/scores', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind: 'marathon', level: 'marathon',
          name: this.settings.get('handle'), time,
        }),
      });
      const body = await res.json().catch(() => null);
      if (body?.ok && body.improved && body.rank) {
        this.hud.showToast(`WORLD RANK #${body.rank}`);
        this._showBoard('marathon', body.board);
      }
    } catch { /* offline is fine; the run is already saved locally */ }
  }

  async _showBoard(level, preloaded = null) {
    // Renders race: the results screen fetch and the post-record render both
    // land here, and the fetch can resolve last with a stale cached board.
    // Whoever started most recently wins; older resolutions are dropped.
    const seq = this._boardSeq = (this._boardSeq ?? 0) + 1;
    let board = preloaded;
    if (!board) {
      this.screens.setBoard(null);
      try {
        const res = await fetch(`/api/scores?level=${encodeURIComponent(level)}`);
        const body = await res.json().catch(() => null);
        if (!body?.ok || !Array.isArray(body.board)) return;
        board = body.board;
      } catch { return; /* board is decoration when offline */ }
    }
    if (seq !== this._boardSeq) return;
    // Stored names are canonical (trimmed, uppercased, spaces collapsed);
    // canonicalise ours the same way or your own row never reads as yours.
    const me = String(this.settings.get('handle') ?? '')
      .trim().toUpperCase().replace(/\s+/g, ' ');
    const rows = board.slice(0, 5).map((e, i) => ({
      rank: i + 1, name: e.n, time: e.t, ghostKey: e.g, me: e.n === me,
      text: e.d != null ? `DEPTH ${e.d}` : undefined,
    }));
    // Off the podium but on the board: show my row too, with its real rank.
    const mineIdx = board.findIndex((e) => e.n === me);
    if (mineIdx >= 5) {
      const e = board[mineIdx];
      rows.push({ rank: mineIdx + 1, name: e.n, time: e.t, ghostKey: e.g, me: true,
        text: e.d != null ? `DEPTH ${e.d}` : undefined });
    }
    this.screens.setBoard(rows,
      (r) => this._raceRemote(level, r),
      (r) => this._watchRemote(level, r));
  }

  async _raceRemote(level, row) {
    try {
      const res = await fetch(`/api/scores/ghost?key=${encodeURIComponent(row.ghostKey)}`);
      const body = await res.json().catch(() => null);
      if (!body?.ok || !body.track) { this.hud.showToast('GHOST UNAVAILABLE', true); return; }
      this._remoteGhost = { level, name: row.name, track: body.track };
      this.startLevel(level);
    } catch {
      this.hud.showToast('GHOST UNAVAILABLE', true);
    }
  }

  // ------------------------------------------------------------- theater
  // WATCH a board ghost: the level runs for real, the hologram replays its
  // run on loop, and the camera chases it instead of the player (who idles
  // at spawn, input off). The ghost IS the proof of the time; the theater
  // just lets you study the proof.

  async _watchRemote(level, row) {
    try {
      const res = await fetch(`/api/scores/ghost?key=${encodeURIComponent(row.ghostKey)}`);
      const body = await res.json().catch(() => null);
      if (!body?.ok || !body.track) { this.hud.showToast('GHOST UNAVAILABLE', true); return; }
      this._remoteGhost = { level, name: row.name, track: body.track };
      this.startLevel(level);
      // After startLevel: it cleared any previous theater and consumed the
      // remote ghost into the rider.
      this._theater = { name: row.name, track: body.track };
      this.input.setEnabled(false);
      this.rival?.setEnabled(false);
      this.rivalBrain?.abort();
      this._theaterKey = (e) => { if (e.code === 'Escape') this._endTheater(); };
      window.addEventListener('keydown', this._theaterKey);
    } catch {
      this.hud.showToast('GHOST UNAVAILABLE', true);
    }
  }

  _clearTheater() {
    if (!this._theater) return;
    this._theater = null;
    window.removeEventListener('keydown', this._theaterKey);
  }

  _endTheater() {
    this._clearTheater();
    this.ghostRider.stop();
    this.exitToMenu('levels');
  }

  _updateTheater() {
    const rider = this.ghostRider;
    const track = this._theater.track;
    // Loop for study: a beat after the run dissolves, it runs again.
    const last = track.samples[track.samples.length - 1][0];
    if (rider.t > last + 2.4) rider.start(track);
    // Chase cam: the spectate framing, aimed at the hologram's head.
    const yaw = rider.group.rotation.y;
    this.camera.position.set(
      rider.group.position.x + Math.sin(yaw) * 6,
      rider.group.position.y + 3.2,
      rider.group.position.z + Math.cos(yaw) * 6,
    );
    this.camera.lookAt(
      rider.group.position.x, rider.group.position.y + 1.4, rider.group.position.z,
    );
    this.hud.setObjective(
      `THEATER // ${this._theater.name} // ${track.time.toFixed(2)}S // LOOPS // ESC EXITS`);
  }

  /**
   * Player feedback: the form's text plus the context that makes a report
   * actionable (sector, version, device class, current fps), all data the
   * telemetry whitelist already allows. The worker validates, stores and
   * pings; the AE row is written server-side so it cannot be skipped.
   */
  async sendFeedback({ category, text } = {}) {
    let ok = false;
    let reason = null;
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          category,
          text,
          context: {
            level: this.levelDef?.id ?? '',
            version: pkg.version,
            tier: this.engine.tier?.name ?? '',
            fps: Math.round(this.engine.fps),
            touch: !!this.input.touch,
          },
        }),
      });
      const body = await res.json().catch(() => null);
      ok = body?.ok === true;
      if (!ok) reason = body?.reason ?? null;
    } catch { /* offline; the form says try later */ }
    this.screens.feedbackDone(ok, reason);
    return ok;
  }

  /**
   * One perf datapoint per session, a minute of PLAY in (menus idle at a
   * different cost and would skew the fleet picture). Engine.fps is the
   * rolling 60-frame mean the HUD already trusts.
   */
  _maybeFpsSample(elapsed) {
    if (this._fpsSampled || elapsed < 60) return;
    this._fpsSampled = true;
    track('fps_sample', {
      value: Math.round(this.engine.fps),
      meta: `${this.engine.tier?.name ?? ''}${this.input.touch ? ' touch' : ' desktop'}`,
    });
  }

  onLevelFailed(reason) {
    if (this.state === STATE.RESULT) return;
    // Value = depth for tower runs: failure is the NORMAL end of a gauntlet,
    // so the tower's difficulty curve lives in this row, not in completions.
    track('level_fail', {
      level: this.levelDef?.id ?? '',
      value: this.mode?.depth ?? 0,
      meta: String(reason).slice(0, 60),
    });
    this.state = STATE.RESULT;
    this.engine.paused = true;
    this.input.setEnabled(false);
    this.audio.setAmbience({ speed: 0, active: false });
    this.audio.fail();
    this.screens.showDead(reason, { canRespawn: !this.mode?.finished });
  }

  // -------------------------------------------------------------- game loop

  update(dt, elapsed) {
    if (this.state !== STATE.PLAYING || !this.level) {
      this.sky?.update(dt, elapsed, this.camera);
      this.postfx?.update(dt, elapsed, 0);
      this.hud?.update(dt);
      return;
    }
    this._maybeFpsSample(elapsed);

    const input = this.input.sample();
    if (input.pause) { this.pause(); return; }

    this.player.look(input.lookX, input.lookY);
    if (input.respawn) this.respawnPlayer();
    if (input.clearPortals) {
      this.portals.clear('player');
      if (this.race?.open) this.race.sendPortalClear();
    }
    if (this._spectating) {
      if (input.fireA || input.fireB) this._spectateCycle = true;
      if (input.jumpPressed) this._spectating.pov = !this._spectating.pov;
    } else {
      if (input.fireA) this.firePortal('a');
      if (input.fireB) this.firePortal('b');
    }

    if (!this.save.data.tut) this._updateTutorial(dt, input);
    else this._updateStranded(dt);

    this.player.update(dt, input);
    this.portals.updateTraversal(dt);

    const ctx = this.gameContext(dt);
    this.level.update(dt, ctx);
    const pc = this.player.controller;
    this.level.testTriggers(
      sweptPoints(this._prevPlayerPos, pc, _sweepPool, _sweepList, this._playerCut || pc.justTeleported),
      ctx,
    );
    this._prevPlayerPos.copy(this.player.position);
    this._playerCut = false;
    pc.justTeleported = false;

    if (this.player.position.y < this.level.killY) {
      this.killPlayer('The void took you.');
    }

    this._updateRival(dt);
    this.ghostRecorder.update(dt, this.player.position, this.player.yaw);
    this.ghostRider.update(dt);
    if (this._theater) this._updateTheater();
    this.remotes.update(dt, this.player.eye(_v));
    if (this.race) {
      this.race.tick(dt, this.player, this.levelDef?.id ?? '');
      if (this._raceGateUntil && performance.now() < this._raceGateUntil) {
        const left = Math.ceil((this._raceGateUntil - performance.now()) / 1000);
        if (left !== this._raceCount) {
          this._raceCount = left;
          this.hud.showToast(String(left), false, 0.9);
        }
        this.player.controller.frozen = true;
      } else if (this._raceGateUntil) {
        this._raceGateUntil = 0;
        this.player.controller.frozen = false;
        this.hud.showToast('GO', false, 0.8);
      }
    }
    this.mode.update(dt);
    this.portals.update(dt, elapsed, this.camera);
    this.sky.update(dt, elapsed, this.camera);

    const speed = this.player.speed;
    this.postfx.update(dt, elapsed, Math.min(1, Math.max(0, (speed - 12) / 55)));
    this.audio.setAmbience({ speed, active: true });

    this._updateLandingMarker(dt, elapsed);
    this.updateHud(dt, speed);
    if (this._spectating) this._updateSpectate(dt);
  }

  // ------------------------------------------------------------------ rival

  setRivalEnabled(on) {
    // `on` is the setting value: 0 off, 1 EASY, 2 NORMAL, 3 BRUTAL.
    // Booleans (tests, old saves) mean full capability.
    const skill = on === true ? 3 : Number(on) || 0;
    this._rivalOn = skill > 0;
    if (!this.rival) return;
    this.rivalAgent.setSkill(skill);
    this.rival.setEnabled(this._rivalOn);
    if (!this._rivalOn) {
      this.rivalBrain.abort();
      this.hud.setRival(null);
    } else if (this.level) {
      this._startRival();
    }
  }

  _startRival() {
    const skill = Number(this.settings.get('rival')) || 0;
    const on = skill > 0;
    this._rivalOn = on;
    this.rivalAgent.setSkill(skill);
    this.rival.setEnabled(on);
    this.hud.setRival(null);
    if (!on || !this.level) return;

    this.rivalAgent.survey(this.level);
    this.rivalAgent.setRoute(RIVAL_ROUTES[this.levelDef?.id] ?? null);
    this.rivalBrain.reset();
    // Offset the spawn so the two of you are not standing inside each other.
    const s = this.level.spawn;
    _v.copy(s.position).add(new THREE.Vector3(Math.cos(s.yaw) * 2.4, 0, -Math.sin(s.yaw) * 2.4));
    this.rival.spawn(_v, s.yaw);
    this.rivalVoice.reset();
    this._rivalShards = null;
    this._leadSide = 0;
    this._leadHold = 0;
    this._leadSaid = 0;
    this.rivalVoice.say('race_start', { level: this.levelDef?.name });
  }

  _updateRival(dt) {
    if (!this._rivalOn || !this.rival.enabled) { this.hud.setRival(null); return; }

    const input = this.rivalAgent.update(dt);
    this.rival.update(dt, input);

    // The rival taking a shard is worth a word; the count is the only
    // signal (pickup triggers are the player's, the agent self-collects).
    const got = this.rivalAgent.targets.filter((t) => t.kind === 'shard' && t.taken).length;
    if (this._rivalShards == null) this._rivalShards = got;
    if (got > this._rivalShards) {
      this._rivalShards = got;
      this.rivalVoice.say('shard', {
        level: this.levelDef?.name,
        shardsLeft: this.rivalAgent.targets.filter((t) => t.kind === 'shard' && !t.taken).length,
        raceSeconds: this.mode?.time ?? 0,
      });
    }

    // Pads and lifts serve the rival too (triggers flagged anyBody). The
    // handlers act on c.player.controller, so the rival's controller is
    // shimmed in under that name; the bus is silenced so the player does
    // not hear or see boost feedback for a launch that is not theirs.
    const rc = this.rival.controller;
    const rpts = sweptPoints(this._prevRivalPos, rc, _rsweepPool, _rsweepList,
      this._rivalCut || rc.justTeleported);
    this._prevRivalPos.copy(rc.position);
    this._rivalCut = false;
    rc.justTeleported = false;
    this.level.testTriggers(rpts, {
      dt,
      bus: _silentBus,
      player: { controller: rc },
      portals: this.portals,
      level: this.level,
      setCheckpoint() {},
      completeLevel() {},
      // Hazards kill the rival the way they kill the player. The old no-op
      // meant a rival that landed in a hazard-floored pit above the kill
      // plane stood in it for the rest of the race.
      killPlayer: () => { this._rivalDead = true; },
    }, true);
    // The race shape, for the one decision the brain still makes: which
    // line to take at a branch. Positive gap means the rival is ahead.
    const gp = this._goalPoint;
    const gap = gp
      ? this.player.position.distanceTo(gp) - this.rival.position.distanceTo(gp)
      : 0;
    this.rivalBrain.update(dt, {
      playerPosition: this.player.position,
      mode: this.levelDef?.mode?.type ?? 'sprint',
      gapMetres: gap,
      leading: gap > 0,
      raceSeconds: this.mode?.time ?? 0,
    });

    // Death is not a fail state for the rival, just a restart: the void,
    // a hazard, or the agent's own verdict that it is stranded (which is
    // exactly when a human player would reset).
    if (this._rivalDead || this.rival.position.y < this.level.killY
      || this.rivalAgent.takeStranded()) {
      this._rivalDead = false;
      this.rival.spawn(this.level.spawn.position, this.level.spawn.yaw);
      this._rivalCut = true;
      // Death is the one legitimate mid-goal interrupt. The route restarts
      // from the top; shard-tagged steps skip themselves once collected.
      this.rivalAgent.routeReset();
      this.rivalAgent.setPlan([{ kind: 'wait', seconds: 0.6 }], { force: true });
      this.rivalVoice.say('rival_died', {
        level: this.levelDef?.name,
        raceSeconds: this.mode?.time ?? 0,
      });
    }

    if (this._goalPoint) {
      // On collect sectors the rival plays by the player's rule: the gate
      // only counts with a full satchel. Both planners already order
      // shards first, but cold-benched shards could otherwise let it
      // loiter at the gate into a hollow win.
      const owes = this.levelDef?.mode?.type === 'collect'
        && this.rivalAgent.targets.some((t) => t.kind === 'shard' && !t.taken);
      if (!owes && !this.rival.finished && this.rival.position.distanceTo(this._goalPoint) < 3.2) {
        this.rival.finished = true;
        this.rival.finishTime = this.mode?.time ?? 0;
        this.hud.showToast('RIVAL REACHED THE GATE', true, 2.4);
        this.rivalVoice.say('rival_won', {
          level: this.levelDef?.name,
          raceSeconds: this.mode?.time ?? 0,
        }, { once: true });
      }
      const mine = this.player.position.distanceTo(this._goalPoint);
      const theirs = this.rival.finished ? 0 : this.rival.position.distanceTo(this._goalPoint);
      this.hud.setRival({ lead: theirs - mine, distance: theirs });
      this._voiceRace(dt, mine, theirs);
    }
  }

  /**
   * Turn the race into events worth commenting on. The lead flip needs a
   * real margin and a dwell: at the line where the two distances cross,
   * an unguarded comparison flickers every frame, and the rival would
   * narrate every wobble of a shared corridor.
   */
  _voiceRace(dt, mine, theirs) {
    const voice = this.rivalVoice;
    voice.update(dt);
    if (!this._rivalOn || this.rival.finished) return;

    const ahead = theirs < mine - 8;
    const behind = theirs > mine + 8;
    const side = ahead ? 1 : behind ? -1 : 0;
    if (side !== 0 && side === this._leadSide) {
      this._leadHold = (this._leadHold ?? 0) + dt;
    } else {
      this._leadSide = side;
      this._leadHold = 0;
    }
    const facts = () => ({
      level: this.levelDef?.name,
      gapMetres: Math.abs(theirs - mine),
      raceSeconds: this.mode?.time ?? 0,
      shardsLeft: this.rivalAgent.targets.filter((t) => t.kind === 'shard' && !t.taken).length,
    });

    if (this._leadHold > 1.5 && side !== this._leadSaid) {
      this._leadSaid = side;
      voice.say(side > 0 ? 'took_lead' : 'lost_lead', facts());
    }
    // The last stretch, once: close to the gate and actually racing for it.
    if (theirs < 40 && mine < 90) voice.say('near_finish', facts(), { once: true });
  }

  gameContext(dt) {
    return {
      dt,
      bus: this.bus,
      player: this.player,
      portals: this.portals,
      level: this.level,
      mode: this.mode,
      // REDLINE has no checkpoints: a death is a walk back from spawn.
      setCheckpoint: (p, yaw) => { if (!this._redline) this.player.setCheckpoint(_v.fromArray(p), yaw); },
      completeLevel: () => this.mode?.onGoal(),
      killPlayer: (reason) => this.killPlayer(reason),
    };
  }

  updateHud(dt, speed) {
    const hud = this.hud;
    const m = this.mode.hud();
    hud.setObjective(this._marathon
      ? `MARATHON ${this._marathon.idx + 1}/${LEVELS.length} // BANKED ${this._marathon.total.toFixed(0)}S`
      : (m.objective ?? ''));
    const left = this.mode.redlineLeft;
    if (left != null) hud.setTimer(left, { countdown: true, danger: left < 10 });
    else if (m.countdown != null) hud.setTimer(m.countdown, { countdown: true, danger: m.danger });
    else hud.setTimer(this.mode.time);
    if (m.shards) hud.setShards(m.shards.got, m.shards.total);

    hud.setSpeed(speed);
    hud.setAltitude(this.player.position.y, this.level.spawn.position.y);
    const pair = this.portals.pair('player');
    hud.setPortals(pair.a.active, pair.b.active);
    hud.setCrosshair(this._aimState());

    if (this._flingTimer > 0) {
      this._flingTimer -= dt;
      if (this._flingTimer <= 0 && this._flingPeak > 26) {
        hud.showCombo(`${this._flingPeak.toFixed(0)} M/S`);
        this._flingPeak = 0;
      }
    }
    hud.setFps(this.engine.fps, !!this.settings.get('showFps'));
    hud.update(dt);
  }

  _aimState() {
    // Cheap enough to run every frame and it is the main teaching signal for
    // which surfaces will take a portal.
    const origin = this.player.eye(_v).clone();
    const dir = this.player.forward().clone();
    const hit = this.world.raycast(origin, dir, 140, (c) => !c.hasTag('portal-filler'), { includeDisabled: true });
    if (!hit) return '';
    return hit.collider.portalable ? 'valid' : 'invalid';
  }

  firePortal(which) {
    const origin = this.player.eye(_v).clone();
    let dir = this.player.forward().clone();
    dir = this._assistFallingAim(origin, dir) ?? dir;
    this.audio.portalFire(which);
    const res = this.portals.tryPlace('player', which, origin, dir, dir);
    if (res.ok && this.race?.open) {
      const placed = this.portals.portal('player', which);
      this.race.sendPortal(which, this.levelDef?.id ?? '', placed.group.position, placed.group.quaternion);
    }
    if (!res.ok) {
      this.bus.emit(EV.PORTAL_DENIED, res);
      this.hud.showToast(res.reason, true, 1.2);
    }
  }

  /**
   * Falling aim assist. The game's signature move is putting a portal under
   * yourself mid-fall, and at 40 m/s nobody can hand-place a two-metre
   * ellipse on their ballistic impact point; playtests (and Reddit) read
   * that as "the aim is off". While falling fast and aiming downward, the
   * shot is retargeted at the computed impact point: steep aims always
   * (the intent is unambiguous), shallow aims only when the shot already
   * lands near the impact point, so deliberate wall shots stay untouched.
   * Returns a replacement direction, or null to leave the shot alone.
   */
  _assistFallingAim(origin, dir) {
    const c = this.player.controller;
    if (c.grounded || c.velocity.y > -10 || dir.y > -0.25) return null;

    const floor = this._ballisticImpact();
    if (!floor || floor.normal.y < 0.7 || !floor.collider.portalable) return null;
    _impact.copy(floor.point);

    if (dir.y > -0.6) {
      // Shallow aim: only pull a shot that was already trying for the spot.
      const shot = this.world.raycast(origin, dir, 90,
        (col) => !col.hasTag('portal-filler'), { includeDisabled: true });
      if (!shot || shot.point.distanceTo(_impact) > 6) return null;
    }
    return _aim.subVectors(_impact, origin).normalize().clone();
  }

  /**
   * The landing marker: a soft ring on the surface this fall will hit.
   * Steering toward a target while looking straight down loses the
   * reference frame ("micro-adjustments back and forth"); the ring closes
   * the loop by showing where the current arc actually ends.
   */
  _makeLandingMarker() {
    // A landing light's soft glow in fog, not a hard decal: a canvas
    // radial gradient on an additive plane, with a barely-there ring for
    // aim precision. Still zero assets.
    const cnv = document.createElement('canvas');
    cnv.width = cnv.height = 128;
    const x = cnv.getContext('2d');
    const grad = x.createRadialGradient(64, 64, 4, 64, 64, 62);
    grad.addColorStop(0, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = grad;
    x.fillRect(0, 0, 128, 128);
    const tex = new THREE.CanvasTexture(cnv);

    const group = new THREE.Group();
    // Normal blending, not additive: an additive glow washes out to
    // nothing against lit panels, which are exactly the surfaces players
    // land on (the vanishing-replica lesson, relearned as "the target
    // never renders on the platform").
    const glow = new THREE.Mesh(
      new THREE.PlaneGeometry(3.4, 3.4),
      new THREE.MeshBasicMaterial({
        map: tex, color: 0x2ff3ff, transparent: true, opacity: 0.5,
        side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
      }),
    );
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.9, 1.0, 40),
      new THREE.MeshBasicMaterial({
        color: 0x2ff3ff, transparent: true, opacity: 0.16,
        side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
      }),
    );
    glow.userData.baseOpacity = 0.5;
    ring.userData.baseOpacity = 0.28;
    // Children lie flat in the group's XZ plane; the group's quaternion
    // aligns +Y to the hit surface's normal each frame.
    glow.rotation.x = -Math.PI / 2;
    ring.rotation.x = -Math.PI / 2;
    group.add(glow, ring);
    group.visible = false;
    this.scene.add(group);
    this._predVel = new THREE.Vector3();
    this._markerAnchor = new THREE.Vector3();
    this._markerFade = 0;
    return group;
  }

  _updateLandingMarker(dt, elapsed) {
    const m = this._marker;
    if (!m) return;
    const c = this.player.controller;
    // Prediction reads a SMOOTHED horizontal velocity: from high up the
    // landing time multiplies every steering wiggle into metres, and the
    // raw value whipped the light across the pit ("fast jerking motion").
    // Vertical stays exact, or the fall time itself would lie.
    // A teleport-scale position jump means the smoothing memory is from a
    // different flight: snap it, or the light predicts with stale motion
    // for the first half second after every respawn and portal.
    // Launch pads REPLACE velocity in one frame; that discontinuity is a
    // teleport in velocity space, and smoothing across it predicts with
    // the pre-launch motion for a fifth of a second.
    if (this._markerAnchor.distanceToSquared(c.position) > 225
      || this._predVel.distanceToSquared(c.velocity) > 225) {
      this._predVel.copy(c.velocity);
      // New flight, new light: fade out and re-bloom at the fresh answer
      // instead of sweeping across the map from the previous flight's.
      this._markerFade = 0;
    } else {
      const k = 1 - Math.exp(-dt / 0.2);
      this._predVel.x += (c.velocity.x - this._predVel.x) * k;
      this._predVel.z += (c.velocity.z - this._predVel.z) * k;
      this._predVel.y = c.velocity.y;
    }
    this._markerAnchor.copy(c.position);

    // The settings toggle is a switch, not a light: honor it instantly.
    if (this.settings.get('marker') === 0) {
      this._markerFade = 0;
      m.visible = false;
      return;
    }
    // Descending is the whole gate. A minimum-drop threshold used to
    // suppress hop flicker, but it REJECTED the platform of any low leap
    // (drop under 4m for most of the arc) while accepting the deep ground
    // beyond it: the light literally preferred the wrong surface ("the
    // marker is projected on the ground below"). The fade keeps brief hop
    // glows gentle; honesty does the rest.
    const show = !c.grounded && c.velocity.y < -1 && !this._spectating;
    let placed = false;
    let danger = false;
    if (show) {
      const floor = this._ballisticImpact(this._predVel);
      if (floor) {
        _impact.copy(floor.point).addScaledVector(floor.normal, 0.06);
        m.quaternion.setFromUnitVectors(_up, floor.normal);
        placed = true;
      } else {
        // No surface anywhere on this course: the arc clears everything
        // and ends in the void. That is an ANSWER, not an absence: the
        // light dives to where the course crosses the kill plane and
        // burns red. (Shown as a disappearance, honest data read as
        // flicker: "the marker is nowhere to be seen".)
        const killY = (this.level?.killY ?? -400);
        const drop = c.position.y - killY;
        if (drop > 0) {
          const g = Math.abs(c.gravity) * (c._gravHold > 0 ? c._gravScale : 1);
          const vy = c.velocity.y;
          const t = (vy + Math.sqrt(Math.max(0, vy * vy + 2 * g * drop))) / g;
          _impact.set(
            c.position.x + this._predVel.x * t,
            killY + 2,
            c.position.z + this._predVel.z * t,
          );
          m.quaternion.setFromUnitVectors(_up, _up);
          placed = true;
          danger = true;
        }
      }
      if (placed) {
        if (this._markerFade <= 0) {
          m.position.copy(_impact);
        } else {
          // A landing light SWEEPS to its new answer at a bounded speed;
          // it never teleports across the pit.
          const dist = m.position.distanceTo(_impact);
          // Distance-proportional but speed-capped: a pit-depth change of
          // answer is a visible dive (~1s down the shaft), not a blink.
          const stepLen = Math.min(dist, Math.min(150, 6 + dist * 4) * dt);
          if (dist > 1e-4) m.position.lerp(_impact, stepLen / dist);
        }
        const pulse = 1 + Math.sin(elapsed * 6) * 0.05;
        m.scale.setScalar(pulse);
      }
    }
    for (const child of m.children) {
      child.material.color.setHex(danger ? 0xff4433 : 0x2ff3ff);
    }
    // Fade like a light, never pop like a sprite; out is brisker than in
    // (a light lingering over ground you already stand on reads as lag).
    const target = placed ? 1 : 0;
    this._markerFade += (target - this._markerFade) * Math.min(1, dt * (placed ? 8 : 15));
    if (this._markerFade < 0.05 && !placed) this._markerFade = 0;
    m.visible = this._markerFade > 0.02;
    for (const child of m.children) {
      child.material.opacity = child.userData.baseOpacity * this._markerFade;
    }
  }

  /**
   * Where this fall lands: two-pass ballistic estimate shared by the aim
   * assist and the landing marker. Fall time to the floor under the feet,
   * then a re-cast at the drifted landing column in case the drift crosses
   * onto different geometry. Effective gravity honors fields and mutators.
   */
  _ballisticImpact(vel = null) {
    const c = this.player.controller;
    const filter = (col) => !col.hasTag('portal-filler');
    const g = Math.abs(c.gravity) * (c._gravHold > 0 ? c._gravScale : 1);
    // March the actual arc in segments instead of anchoring on the column
    // under the feet: gliding across a void toward a platform used to
    // return nothing until the flier was already above it ("the marker is
    // nowhere until the moment before contact").
    const vx = vel ? vel.x : c.velocity.x;
    const vz = vel ? vel.z : c.velocity.z;
    let vy = c.velocity.y;
    let px = c.position.x;
    let py = c.position.y;
    let pz = c.position.z;
    // Graded horizon: fine steps near the flier (surface precision where
    // it is about to matter), coarse steps far out, ~7.7s total. The old
    // flat 4.8s horizon expired mid-estimate at the top of high falls and
    // the light blinked out, then SNAPPED back in ("leaps to the far end").
    for (let i = 0; i < 48; i++) {
      const step = i < 8 ? 0.06 : 0.18;
      const nvy = vy - g * step;
      const nx = px + vx * step;
      const ny = py + (vy + nvy) * 0.5 * step;
      const nz = pz + vz * step;
      _impact.set(px, py, pz);
      _aim.set(nx - px, ny - py, nz - pz);
      const len = _aim.length();
      if (len > 1e-4) {
        _aim.multiplyScalar(1 / len);
        const hit = this.world.raycast(_impact, _aim, len, filter, { includeDisabled: true });
        if (hit) return hit;
      }
      px = nx; py = ny; pz = nz; vy = nvy;
      if (py < (this.level?.killY ?? -400) - 20) return null;
    }
    return null;
  }

  respawnPlayer() {
    this._playerCut = true;
    if (this._stranded?.shown) this.hud.setHint(null);
    this._stranded = null;
    this.player.respawn();
    this.postfx.flash(0.35);
    this.hud.showToast('REBOOT');
  }

  killPlayer(reason) {
    if (this.state !== STATE.PLAYING) return;
    this._runDeaths = (this._runDeaths ?? 0) + 1;
    if (this._stranded?.shown) this.hud.setHint(null);
    this._stranded = null;
    this.audio.death();
    this.postfx.hurt(1);
    this.hud.hit(0.9);
    this.hud.showToast(reason, true, 1.6);
    this._playerCut = true;
    this.player.respawn();
    if (this._rivalOn && this.rivalVoice) {
      this.rivalVoice.say('player_died', {
        level: this.levelDef?.name,
        raceSeconds: this.mode?.time ?? 0,
      });
    }
  }

  // ----------------------------------------------------------------- render

  render(dt, elapsed) {
    const renderer = this.engine.renderer;
    if (this.state === STATE.PLAYING || this.state === STATE.PAUSED || this.state === STATE.RESULT) {
      this.portals.renderViews(renderer, this.scene, this.camera);
    }
    this.postfx.render(dt);
  }

  dispose() {
    this.unloadLevel();
    this.audio.dispose();
    this.input.dispose();
    this.engine.dispose();
  }
}
